import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import type { PlatformBlockComponent, PlatformBlockType } from "../../../types";
import { BlockFormHeader } from "../BlockFormHeader";
import { TextField } from "@/shared/components/Form/TextField";
import { AutocompleteField } from "@/shared/components/Form/AutocompleteField";
import { ItemsPerRowField } from "../../ItemsPerRowField";

const viewValues = ["CAROUSEL", "GRID"] as const;

const previewModeValues = ["HORIZONTAL", "VERTICAL"] as const;

/**
 * Общая форма персональных полок «Избранное» (FAVORITE_CONTENTS) и
 * «Продолжить просмотр» (CONTINUE_WATCHING_CONTENTS) — настройки референсных
 * FavoriteContents / ContinueWatchingContents форм: заголовок, ссылка «Ещё»,
 * вид (карусель/сетка), карточек в ряд, количество, ориентация превью.
 * Источник данных фиксирован (личные списки зрителя), поэтому фильтров по
 * тегам/сортировке, как у блока «Видео», здесь нет.
 */
const createPersonalContentsForm = (
  blockType: PlatformBlockType
): PlatformBlockComponent => {
  const PersonalContentsForm: PlatformBlockComponent = ({ name, values }) => {
    const { t } = useTranslation();

    const viewOptions = viewValues.map((value) => ({
      value,
      label: t(`platformBlocks.view.${value}`),
    }));

    const previewModeOptions = previewModeValues.map((value) => ({
      value,
      label: t(`platformBlocks.previewMode.${value}`),
    }));

    const props = values?.props as Record<string, unknown> | undefined;

    return (
      <Paper sx={{ p: 2 }}>
        <BlockFormHeader blockType={blockType} props={props} />
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              name={`${name}.title`}
              label={t("platformBlocks.fields.title")}
              type="text"
              fullWidth
            />
          </Grid>
          <Grid size={12}>
            <TextField
              name={`${name}.moreUrlHref`}
              label={t("platformBlocks.personalContents.moreUrl")}
              type="text"
              fullWidth
              helperText={t("platformBlocks.personalContents.moreUrlHelper")}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <AutocompleteField
              name={`${name}.view`}
              label={t("platformBlocks.fields.view")}
              fullWidth
              options={viewOptions}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <AutocompleteField
              name={`${name}.previewMode`}
              label={t("platformBlocks.fields.previewMode")}
              fullWidth
              options={previewModeOptions}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <ItemsPerRowField
              name={`${name}.itemsPerRow`}
              label={t("platformBlocks.fields.itemsPerRow")}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              name={`${name}.limit`}
              label={t("platformBlocks.fields.limit")}
              type="number"
              fullWidth
            />
          </Grid>
        </Grid>
      </Paper>
    );
  };

  return PersonalContentsForm;
};

export const FavoriteContents = createPersonalContentsForm("FAVORITE_CONTENTS");
export const ContinueWatchingContents = createPersonalContentsForm(
  "CONTINUE_WATCHING_CONTENTS"
);
